import { useState, useCallback } from 'react';
import { ApiService } from '../services/api';
import { CertificateUploadState } from '../types/mtls';
import { handleApiError, handleApiSuccess } from '../utils/mtls-error-handler';

export const useFileUpload = () => {
  const [uploadState, setUploadState] = useState<CertificateUploadState>({
    uploading: false,
    progress: 0,
  });

  const uploadCertificate = useCallback(async (
    organizationId: number,
    file: File,
    description?: string,
    certificateType: "prod" | "non-prod" = "non-prod"
  ): Promise<boolean> => {
    try {
      setUploadState({
        file,
        uploading: true,
        progress: 0,
        error: undefined,
        validationErrors: undefined,
      });

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadState(prev => ({
          ...prev,
          progress: Math.min(prev.progress + 10, 90),
        }));
      }, 200);

      await ApiService.uploadMtlsCertificate(organizationId, file, description, certificateType);

      clearInterval(progressInterval);
      
      setUploadState(prev => ({
        ...prev,
        progress: 100,
        uploading: false,
      }));

      handleApiSuccess('Certificate uploaded successfully', 'Certificate Upload');
      
      // Reset state after success
      setTimeout(() => {
        setUploadState({
          uploading: false,
          progress: 0,
        });
      }, 2000);

      return true;
    } catch (error: any) {
      setUploadState(prev => ({
        ...prev,
        uploading: false,
        progress: 0,
        error: error.message || 'Upload failed',
        validationErrors: error.details?.reduce((acc: Record<string, string>, detail: any) => {
          acc[detail.field] = detail.message;
          return acc;
        }, {}) || undefined,
      }));

      handleApiError(error, 'Certificate Upload');
      return false;
    }
  }, []);

  const resetUpload = useCallback(() => {
    setUploadState({
      uploading: false,
      progress: 0,
    });
  }, []);

  const retryUpload = useCallback(async () => {
    if (uploadState.file) {
      // You would need to store organizationId and description to retry
      // For now, just reset the error state
      setUploadState(prev => ({
        ...prev,
        error: undefined,
        validationErrors: undefined,
      }));
    }
  }, [uploadState.file]);

  return {
    uploadState,
    uploadCertificate,
    resetUpload,
    retryUpload,
  };
};